import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Network, Pencil, Trash2, BellRing, Check, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Spinner } from "@/components/Kit";
import { GradeChip } from "@/components/WebKit";
import { SpoofChip } from "@/components/AttackKit";

export default function Domains() {
  const { orgId } = useOrg();
  const { can } = useAuth();
  const canManage = can("attack.domains", "manage");
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(() => http.get("/attack/domains", { params: { org_id: orgId } }).then((r) => setDomains(r.data)), [orgId]);
  const loadArchived = useCallback(() => http.get("/attack/domains", { params: { org_id: orgId, archived: true } }).then((r) => setArchived(r.data)).catch(() => setArchived([])), [orgId]);
  const loadAlerts = useCallback(() => http.get("/attack/alerts", { params: { org_id: orgId, unacknowledged_only: true } }).then((r) => setAlerts(r.data)).catch(() => setAlerts([])), [orgId]);
  useEffect(() => { setDomains([]); setArchived([]); setAlerts([]); }, [orgId]);
  useEffect(() => { if (orgId) { load(); loadArchived(); loadAlerts(); } }, [orgId, load, loadArchived, loadAlerts]);

  const ackAlert = async (a) => {
    try { await http.patch(`/attack/alerts/${a.id}`, { acknowledged: true }, { params: { org_id: orgId } }); loadAlerts(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const archive = async (d, e) => {
    e.stopPropagation();
    if (!window.confirm(`Archive ${d.registrable_domain}? Monitoring will pause and it will be hidden from the active list. You can restore it any time — its records are kept.`)) return;
    try { await http.post(`/attack/domains/${d.id}/archive`, null, { params: { org_id: orgId } }); toast.success("Domain archived"); load(); loadArchived(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const restore = async (d) => {
    try { await http.post(`/attack/domains/${d.id}/unarchive`, null, { params: { org_id: orgId } }); toast.success("Domain restored"); load(); loadArchived(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (d, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Permanently delete ${d.registrable_domain} and ALL its records — email posture, history, assets, CT certificates, exposure findings, monitoring watchers, snapshots and alerts? This cannot be undone.`)) return;
    try { await http.delete(`/attack/domains/${d.id}`, { params: { org_id: orgId } }); toast.success("Domain and all its records deleted"); load(); loadArchived(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const [sel, setSel] = useState(new Set());
  const [selArch, setSelArch] = useState(new Set());
  const toggle = (set, setFn, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setFn(n); };
  const allChecked = (rows, set) => rows.length > 0 && rows.every((d) => set.has(d.id));
  const toggleAll = (rows, set, setFn) => setFn(allChecked(rows, set) ? new Set() : new Set(rows.map((d) => d.id)));

  const bulk = async (ids, action, label) => {
    const list = [...ids];
    if (!list.length) return;
    const warn = action === "delete" ? " This removes ALL their records and cannot be undone." : "";
    if (!window.confirm(`${label} ${list.length} domain${list.length > 1 ? "s" : ""}?${warn}`)) return;
    try {
      const { data } = await http.post("/attack/domains/bulk", { org_id: orgId, ids: list, action });
      toast.success(`${data.processed} domain${data.processed === 1 ? "" : "s"} ${action}d`);
      setSel(new Set()); setSelArch(new Set()); load(); loadArchived();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const daysUntil = (iso) => { if (!iso) return null; return Math.ceil((new Date(iso) - Date.now()) / 86400000); };

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Domains</h1>
          <p className="text-sm text-[#8A8175] mt-1">
            {domains.length} registrable domain{domains.length === 1 ? "" : "s"} under attack-surface &amp; email-security monitoring.
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShow(true)} data-testid="new-domain-btn"
            className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D]">
            <Plus className="w-4 h-4" /> Add Domain
          </button>
        )}
      </div>

      {alerts.length > 0 && (
        <Card className="mb-5 border-[#A5300F]" data-testid="alerts-banner">
          <div className="px-5 py-3 border-b border-[#F0D9D0] bg-[#FCF1EE] flex items-center gap-2">
            <BellRing className="w-4 h-4 text-[#A5300F]" />
            <SectionTitle className="text-[#A5300F]">Monitoring Alerts ({alerts.length})</SectionTitle>
          </div>
          <div className="divide-y divide-[#F4EFE8]">
            {alerts.map((a) => (
              <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`alert-${a.id}`}>
                <div className="min-w-0">
                  <span className="text-[11px] font-bold uppercase tracking-wider mr-2" style={{ color: a.severity === "Critical" ? "#A5300F" : "#D55816" }}>{a.severity}</span>
                  <span className="font-semibold text-sm">{a.domain}</span>
                  <span className="text-sm text-[#5C554C] ml-2">{a.message}</span>
                  <span className="text-[11px] text-[#B5AC9E] ml-2">{new Date(a.created_at).toLocaleString("en-GB")}</span>
                </div>
                <button onClick={() => ackAlert(a)} data-testid={`ack-alert-${a.id}`}
                  className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#8A8175] hover:text-[#A5300F] border border-[#E7D9C6] rounded px-2 py-1">
                  <Check className="w-3.5 h-3.5" /> Acknowledge
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {canManage && sel.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-[#FFF5EB] border border-[#E7C9B5] rounded-md" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-[#A5300F]">{sel.size} selected</span>
          <button onClick={() => bulk(sel, "archive", "Archive")} data-testid="bulk-archive-btn" className="flex items-center gap-1 text-xs font-semibold text-[#8A8175] hover:text-[#A5300F] border border-[#E7D9C6] rounded px-2.5 py-1"><Archive className="w-3.5 h-3.5" /> Archive selected</button>
          <button onClick={() => bulk(sel, "delete", "Permanently delete")} data-testid="bulk-delete-btn" className="flex items-center gap-1 text-xs font-semibold text-[#A5300F] border border-[#E7C9B5] rounded px-2.5 py-1"><Trash2 className="w-3.5 h-3.5" /> Delete selected</button>
          <button onClick={() => setSel(new Set())} className="text-xs text-[#8A8175] underline ml-auto">Clear</button>
        </div>
      )}

      <Card>
        <table className="w-full text-sm" data-testid="domains-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            {canManage && <th className="py-2.5 pl-4 w-8"><input type="checkbox" checked={allChecked(domains, sel)} onChange={() => toggleAll(domains, sel, setSel)} className="accent-[#A5300F]" data-testid="select-all-domains" /></th>}
            <th className="py-2.5 px-4">Domain</th><th>Label</th><th className="text-center">Risk</th><th className="text-center">Email grade</th>
            <th>Spoofability</th><th className="text-center">Monitoring</th><th>Last email scan</th>{canManage && <th className="text-right pr-4">Actions</th>}
          </tr></thead>
          <tbody>
            {domains.map((d) => (
              <tr key={d.id} onClick={() => navigate(`/attack/domains/${d.id}`)}
                className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer" data-testid={`domain-row-${d.id}`}>
                {canManage && <td className="pl-4" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(sel, setSel, d.id)} className="accent-[#A5300F]" data-testid={`select-domain-${d.id}`} /></td>}
                <td className="py-3 px-4 font-semibold flex items-center gap-2"><Network className="w-4 h-4 text-[#B5AC9E]" />{d.registrable_domain}</td>
                <td className="text-[#5C554C]">{d.label}</td>
                <td className="text-center" data-testid={`risk-${d.id}`}><GradeChip grade={d.risk_grade} score={d.risk_score} /></td>
                <td className="text-center"><GradeChip grade={d.email_grade} score={d.email_score} /></td>
                <td><SpoofChip verdict={d.spoofability} /></td>
                <td className="text-center text-xs">{d.monitoring_enabled ? "On" : "Off"}</td>
                <td className="text-xs text-[#8A8175]">{d.last_email_scan_at ? new Date(d.last_email_scan_at).toLocaleString("en-GB") : "Never"}</td>
                {canManage && (
                  <td className="text-right pr-4 whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); setEdit(d); }} data-testid={`edit-domain-${d.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]" title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={(e) => archive(d, e)} data-testid={`archive-domain-${d.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]" title="Archive (pause monitoring, keep records)"><Archive className="w-4 h-4" /></button>
                    <button onClick={(e) => remove(d, e)} data-testid={`delete-domain-${d.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]" title="Delete permanently (removes all records)"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
            {domains.length === 0 && <tr><td colSpan={canManage ? 9 : 7} className="py-10 text-center text-[#B5AC9E]">No domains yet. Add a registrable domain to begin email-security posture assessment.</td></tr>}
          </tbody>
        </table>
      </Card>

      {canManage && archived.length > 0 && (
        <div className="mt-5">
          <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn"
            className="flex items-center gap-2 text-sm font-semibold text-[#8A8175] hover:text-[#A5300F]">
            <Archive className="w-4 h-4" /> {showArchived ? "Hide" : "Show"} archived domains ({archived.length})
          </button>
          {showArchived && (
            <Card className="mt-3" data-testid="archived-card">
              {selArch.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 bg-[#FFF5EB] border-b border-[#E7C9B5]" data-testid="archived-bulk-bar">
                  <span className="text-sm font-semibold text-[#A5300F]">{selArch.size} selected</span>
                  <button onClick={() => bulk(selArch, "unarchive", "Restore")} data-testid="bulk-restore-btn" className="flex items-center gap-1 text-xs font-semibold text-[#2E7D32] border border-[#CDE5CE] rounded px-2.5 py-1"><ArchiveRestore className="w-3.5 h-3.5" /> Restore selected</button>
                  <button onClick={() => bulk(selArch, "delete", "Permanently delete")} data-testid="bulk-delete-archived-btn" className="flex items-center gap-1 text-xs font-semibold text-[#A5300F] border border-[#E7C9B5] rounded px-2.5 py-1"><Trash2 className="w-3.5 h-3.5" /> Delete selected</button>
                  <button onClick={() => setSelArch(new Set())} className="text-xs text-[#8A8175] underline ml-auto">Clear</button>
                </div>
              )}
              <table className="w-full text-sm" data-testid="archived-domains-table">
                <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
                  <th className="py-2.5 pl-4 w-8"><input type="checkbox" checked={allChecked(archived, selArch)} onChange={() => toggleAll(archived, selArch, setSelArch)} className="accent-[#A5300F]" data-testid="select-all-archived" /></th>
                  <th className="py-2.5 px-4">Domain</th><th>Label</th><th>Archived</th><th>Auto-delete</th><th className="text-right pr-4">Actions</th>
                </tr></thead>
                <tbody>
                  {archived.map((d) => {
                    const days = daysUntil(d.auto_delete_at);
                    return (
                    <tr key={d.id} className="border-b border-[#F4EFE8]" data-testid={`archived-row-${d.id}`}>
                      <td className="pl-4"><input type="checkbox" checked={selArch.has(d.id)} onChange={() => toggle(selArch, setSelArch, d.id)} className="accent-[#A5300F]" data-testid={`select-archived-${d.id}`} /></td>
                      <td className="py-3 px-4 font-semibold flex items-center gap-2 text-[#8A8175]"><Network className="w-4 h-4 text-[#B5AC9E]" />{d.registrable_domain}</td>
                      <td className="text-[#8A8175]">{d.label}</td>
                      <td className="text-xs text-[#8A8175]">{d.archived_at ? new Date(d.archived_at).toLocaleString("en-GB") : "—"}</td>
                      <td className="text-xs" data-testid={`autodelete-${d.id}`}>
                        {days === null ? <span className="text-[#B5AC9E]">—</span>
                          : days <= 7 ? <span className="font-semibold text-[#A5300F]">in {Math.max(0, days)}d ⚠</span>
                          : <span className="text-[#8A8175]">in {days}d</span>}
                      </td>
                      <td className="text-right pr-4 whitespace-nowrap">
                        <button onClick={() => restore(d)} data-testid={`restore-domain-${d.id}`} className="text-[#2E7D32] font-semibold text-xs underline mr-3 inline-flex items-center gap-1"><ArchiveRestore className="w-3.5 h-3.5" /> Restore</button>
                        <button onClick={() => remove(d, null)} data-testid={`delete-archived-${d.id}`} className="text-[#A5300F] hover:opacity-70 align-middle"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {show && <AddDomainModal orgId={orgId} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <EditDomainModal orgId={orgId} domain={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditDomainModal({ orgId, domain, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ label: domain.label || "", monitoring_enabled: !!domain.monitoring_enabled,
    monitoring_interval_hours: domain.monitoring_interval_hours || 24 });
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/attack/domains/${domain.id}`, {
        label: f.label, monitoring_enabled: f.monitoring_enabled,
        monitoring_interval_hours: Number(f.monitoring_interval_hours) || 24,
      }, { params: { org_id: orgId } });
      toast.success("Domain updated"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-md my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>Edit {domain.registrable_domain}</SectionTitle>
          <button onClick={onClose}><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Label"><input data-testid="edit-domain-label" value={f.label} onChange={(e) => setF((p) => ({ ...p, label: e.target.value }))} className={inp} /></Field>
          <label className="flex items-center gap-2 text-sm pt-1">
            <input type="checkbox" data-testid="edit-domain-monitoring" checked={f.monitoring_enabled} onChange={(e) => setF((p) => ({ ...p, monitoring_enabled: e.target.checked }))} className="accent-[#A5300F]" />
            Monitoring enabled (auto re-scan + alert on regression)
          </label>
          {f.monitoring_enabled && (
            <Field label="Interval (hours)"><input type="number" min="1" data-testid="edit-domain-interval" value={f.monitoring_interval_hours} onChange={(e) => setF((p) => ({ ...p, monitoring_interval_hours: e.target.value }))} className={inp} /></Field>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-domain-edit-btn" className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} Save
          </button>
        </div>
      </Card>
    </div>
  );
}

function AddDomainModal({ orgId, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ domain: "", label: "", authorised_by_name: "", authorised_by_email: "", scope_notes: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await http.post("/attack/domains", {
        org_id: orgId, domain: f.domain, label: f.label || undefined,
        authorisation: { authorised_by_name: f.authorised_by_name || undefined,
          authorised_by_email: f.authorised_by_email || undefined, scope_notes: f.scope_notes || undefined },
      });
      toast.success("Domain added");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>Add Domain</SectionTitle>
          <button onClick={onClose} data-testid="close-domain-modal"><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Registrable domain">
            <input data-testid="domain-input" placeholder="example.com" value={f.domain} onChange={(e) => set("domain", e.target.value)} className={inp} />
          </Field>
          <Field label="Label"><input data-testid="domain-label" value={f.label} onChange={(e) => set("label", e.target.value)} className={inp} /></Field>
          <div className="pt-1 text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Authorisation (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Authorised by (name)"><input value={f.authorised_by_name} onChange={(e) => set("authorised_by_name", e.target.value)} className={inp} /></Field>
            <Field label="Email"><input value={f.authorised_by_email} onChange={(e) => set("authorised_by_email", e.target.value)} className={inp} /></Field>
          </div>
          <Field label="Scope notes"><textarea rows={2} value={f.scope_notes} onChange={(e) => set("scope_notes", e.target.value)} className={inp} /></Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy || !f.domain} data-testid="create-domain-btn"
            className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} Add Domain
          </button>
        </div>
      </Card>
    </div>
  );
}

const inp = "w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]";
const Field = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">{label}</label>{children}</div>
);
